import usePendingState from '../../Hooks/usePendingState';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';

import { faArrowLeft, faFile, faImage, faVideo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useNotification } from '../../Contexts/NotificationContext';
import Loading from '../Loading';
import { InputFileImage } from './InputFileImage';

const FormEditProduct = () => {
    const { setNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [productData, setProductData] = useState({
        nome: '',
        img_preview: '',
        lancamento: false,
        manifesto: false,
        notificar: false,
        categoria: '',
    });

    const [productErrorData, setProductErrorData] = useState({
        nome: [],
        img_preview: [],
        lancamento: [],
        manifesto: [],
        notificar: [],
        categoria: [],
    });

    const [formLoading, setFormLoading, formPendingRef] = usePendingState();

    const navigate = useNavigate();
    const { id } = useParams();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProductData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setProductData((prevData) => ({
            ...prevData,
            [name]: selectedOption.value,
        }));
    };

    const handleImageCrop = (croppedImage, fileExtenstion, name) => {
        setProductData((prevData) => ({
            ...prevData,
            [name]: croppedImage,
        }));
    };

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const [productResponse, categoriesResponse] = await Promise.all([
                    fetch(`${baseUrl}/manager/produtos/${id}`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                    fetch(`${baseUrl}/manager/categorias/`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                ]);

                if (productResponse.ok) {
                    const productData = await productResponse.json();
                    setProductData(productData.produto || []);
                }

                if (categoriesResponse.ok) {
                    const categoriesData = await categoriesResponse.json();
                    setCategories(
                        categoriesData.categorias.map((categoria) => ({
                            value: categoria.id,
                            label: categoria.nome,
                        }))
                    );
                }
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formPendingRef.current) return;
        setFormLoading(true);

        try {
            const formData = new FormData();

            Object.keys(productData).forEach((key) => {
                if (key !== 'img_preview') {
                    if (typeof productData[key] === 'boolean') {
                        formData.append(key, productData[key] ? 1 : 0);
                    } else {
                        formData.append(key, productData[key]);
                    }
                }
            });

            if (productData.img_preview) {
                formData.append('img_preview', productData.img_preview);
            }

            const response = await fetch(`${baseUrl}/manager/produtos/atualizar/${id}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: 'Produto atualizado com sucesso!!',
                    show: true,
                });
                navigate('/manager/produtos');
            } else {
                if (response.status === 422) {
                    const errors = await response.json();
                    setProductErrorData(errors);
                } else {
                    throw new Error('Erro ao atualizar produto.');
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    if (loading)
        return (
            <div className="mx-auto py-20 flex justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent" />
            </div>
        );

    return (
        <>
            <Link
                to={`/manager/produtos/materiais/${id}`}
                className="bg-secondary text-white px-4 py-1.5 rounded border border-secondary float-right ml-2 transition-all hover:bg-opacity-80"
            >
                <FontAwesomeIcon icon={faFile} className="mr-2" />
                Materiais
            </Link>

            <Link
                to={`/manager/produtos/imagens/${id}`}
                className="bg-secondary text-white px-4 py-1.5 rounded border border-secondary float-right ml-2 transition-all hover:bg-opacity-80"
            >
                <FontAwesomeIcon icon={faImage} className="mr-2" />
                Imagens
            </Link>

            <Link
                to={`/manager/produtos/videos/${id}`}
                className="bg-secondary text-white px-4 py-1.5 rounded border border-secondary float-right ml-2 transition-all hover:bg-opacity-80"
            >
                <FontAwesomeIcon icon={faVideo} className="mr-2" />
                Vídeos
            </Link>

            <Link
                to="/manager/produtos"
                className="bg-slate-100 px-4 py-1.5 rounded border border-slate-300 float-right ml-2 transition-all hover:bg-slate-200"
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Voltar
            </Link>

            <h4 className="mb-6 text-xl font-bold text-black">Editar produto</h4>

            <div className="flex flex-col">
                <form onSubmit={handleSubmit} className="max-w-5xl pb-10">
                    <div className="mb-6 2xl:mb-10">
                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-3/4">
                                <label className="block text-sm font-medium mb-1">Nome</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={productData.nome}
                                    onClick={() => setProductErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {productErrorData.nome && productErrorData.nome[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {productErrorData.nome[0]}{' '}
                                        <span
                                            onClick={() => setProductErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-2/5">
                                <label className="block text-sm font-medium mb-1">Categoria</label>
                                <Select
                                    name="categoria"
                                    options={categories}
                                    value={categories.find((option) => option.value === productData.categoria) || null}
                                    onClick={() => setProductErrorData((prevData) => ({ ...prevData, categoria: [] }))}
                                    onChange={(selectedOption) => handleSelectChange('categoria', selectedOption)}
                                    placeholder="Selecione uma opção..."
                                    classNamePrefix="admin-select"
                                    className="product-select"
                                />
                                {productErrorData.categoria && productErrorData.categoria[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {productErrorData.categoria[0]}{' '}
                                        <span
                                            onClick={() => setProductErrorData((prevData) => ({ ...prevData, categoria: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/12">
                                <label className="block text-sm font-medium mb-1">Lançamento?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="lancamento"
                                        checked={productData.lancamento}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {productErrorData.lancamento && productErrorData.lancamento[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {productErrorData.lancamento[0]}{' '}
                                        <span
                                            onClick={() => setProductErrorData((prevData) => ({ ...prevData, lancamento: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/6">
                                <label className="block text-sm font-medium mb-1">Notificar usuários?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="notificar"
                                        checked={productData.notificar}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {productErrorData.notificar && productErrorData.notificar[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {productErrorData.notificar[0]}{' '}
                                        <span
                                            onClick={() => setProductErrorData((prevData) => ({ ...prevData, notificar: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-1/2">
                                <label className="block text-sm font-medium mb-1">Imagem prévia</label>

                                <InputFileImage
                                    name="img_preview"
                                    imagem={productData.preview}
                                    size={{ largura: '320', altura: '430' }}
                                    allowCrop={true}
                                    onImageCrop={handleImageCrop}
                                />
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={formLoading}
                        aria-busy={formLoading}
                        className="w-full lg:w-3/4 bg-secondary text-white py-2 px-4 rounded-md hover:bg-opacity-80 disabled:bg-opacity-80 disabled:cursor-not-allowed"
                    >
                        {!formLoading ? 'Salvar' : <Loading />}
                    </button>
                </form>
            </div>
        </>
    );
};

export default FormEditProduct;
